/**
 * Outstanding with ageing -- the screen a trading owner opens first.
 *
 * Parties are sorted biggest-balance-first, because the question being
 * asked is "who do I chase today", not "who is alphabetically first".
 * Expanding a party shows its individual bills; clicking one drills to the
 * voucher that raised it.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Box, Chip, IconButton, Skeleton, Stack, Tab, Tabs, Typography,
  useMediaQuery, useTheme,
} from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useTranslation } from 'react-i18next'

import { fetchOutstanding } from '../api/endpoints'
import { BUCKETS, type Bucket } from '../api/types'
import ReportShell, { asAtLabel } from './ReportShell'
import ReportFooter from './ReportFooter'
import ReportToolbar from './ReportToolbar'
import VoucherDrawer from './VoucherDrawer'
import { useDateRange } from './useDateRange'
import { downloadCsv } from './csv'
import OutstandingCards from '../mobile/OutstandingCards'
import { c, fmtMoney } from '../theme'

const GRID = { xs: '1fr 110px', md: '1fr 108px 108px 108px 108px 108px 118px' }

/** Older buckets read progressively more urgent. */
const TONE: Record<Bucket, string> = {
  current: c.inkMuted, d1_30: c.ink, d31_60: c.warningInk,
  d61_90: c.dangerInk, d90_plus: c.dangerInk,
}

export default function Outstanding({ onLedger }: { onLedger?: (c: string) => void }) {
  const { t, i18n } = useTranslation()
  const { range, setRange, presets } = useDateRange()
  const [kind, setKind] = useState<'receivable' | 'payable'>('receivable')
  const [open, setOpen] = useState<Set<string>>(new Set())
  const [voucher, setVoucher] = useState<number | null>(null)
  const theme = useTheme()
  // Phones get cards; hiding five ageing columns is degradation, not design.
  const isPhone = useMediaQuery(theme.breakpoints.down('md'))

  const q = useQuery({
    queryKey: ['outstanding', kind, range.to],
    queryFn: () => fetchOutstanding({ kind, as_of: range.to }),
  })
  const rep = q.data

  const toggle = (code: string) => setOpen((prev) => {
    const next = new Set(prev)
    next.has(code) ? next.delete(code) : next.add(code)
    return next
  })

  return (
    <>
      <ReportShell titleKey="nav.outstanding"
        period={asAtLabel(range.to, i18n.language, t)}
        toolbar={<>
        <Tabs value={kind} onChange={(_, v) => setKind(v)}
          sx={{ px: 2, minHeight: 38, borderBottom: `1px solid ${c.border}` }}>
          <Tab value="receivable" label={t('outstanding.receivable')}
            sx={{ minHeight: 38 }} />
          <Tab value="payable" label={t('outstanding.payable')}
            sx={{ minHeight: 38 }} />
        </Tabs>

        <ReportToolbar range={range} setRange={setRange} presets={presets} singleDate
          pdf={{ path: 'outstanding.pdf', params: { kind, as_of: range.to } }}
          onExport={rep ? () => downloadCsv(`outstanding-${rep.as_of}`, [
            ['Code', 'Party', 'Total', 'Overdue', ...BUCKETS],
            ...rep.parties.map((p) => [
              p.ledger_code, p.ledger_name, p.total, p.overdue,
              ...BUCKETS.map((b) => p.buckets[b])]),
            ['', 'Total', rep.grand_total, '',
             ...BUCKETS.map((b) => rep.totals[b])],
          ]) : undefined} />
        </>}>

        {/* Column heads -- desktop only */}
        {!isPhone && (
        <Box sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 1, px: 2,
          pb: 0.5, borderBottom: `1px solid ${c.border}` }}>
          <span className="section-label">{t('outstanding.party')}</span>
          {BUCKETS.map((b) => (
            <Box key={b} sx={{ display: { xs: 'none', md: 'block' } }}>
              <span className="section-label" style={{ textAlign: 'end',
                display: 'block' }}>{t(`outstanding.${b}`)}</span>
            </Box>
          ))}
          <span className="section-label" style={{ textAlign: 'end' }}>
            {t('report.total')}</span>
        </Box>
        )}

        {q.isLoading ? (
          <Box sx={{ p: 2 }}><Skeleton /><Skeleton /><Skeleton /></Box>
        ) : !rep?.parties.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
            {t('outstanding.nothing')}
          </Typography>
        ) : isPhone ? (
          <OutstandingCards parties={rep.parties} onVoucher={setVoucher} />
        ) : (
          <Box className="ruled">
            {rep.parties.map((p) => (
              <Box key={p.ledger_code}>
                <Box onClick={() => toggle(p.ledger_code)}
                  sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 1,
                    px: 2, py: 1, alignItems: 'center', cursor: 'pointer',
                    '&:hover': { bgcolor: c.primarySoft } }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}
                    sx={{ minWidth: 0 }}>
                    <IconButton size="small" tabIndex={-1} sx={{ p: 0.25 }}>
                      {open.has(p.ledger_code)
                        ? <ExpandMoreIcon sx={{ fontSize: 17 }} />
                        : <ChevronRightIcon sx={{ fontSize: 17 }} />}
                    </IconButton>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5 }} noWrap
                        onClick={(e) => { e.stopPropagation(); onLedger?.(p.ledger_code) }}>
                        {p.ledger_name}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span className="num" style={{ fontSize: 11,
                          color: c.inkMuted, textAlign: 'start' }}>{p.ledger_code}</span>
                        {Number(p.overdue) > 0 && (
                          <Chip size="small" label={t('outstanding.overdueChip',
                            { amount: fmtMoney(p.overdue) })}
                            sx={{ height: 17, fontSize: 9.5,
                              bgcolor: c.dangerSoft, color: c.dangerInk }} />
                        )}
                        {Number(p.on_account) < 0 && (
                          <Chip size="small" label={t('outstanding.advance')}
                            sx={{ height: 17, fontSize: 9.5,
                              bgcolor: c.primarySoft, color: c.primaryInk }} />
                        )}
                      </Stack>
                    </Box>
                  </Stack>

                  {BUCKETS.map((b) => (
                    <Box key={b} sx={{ display: { xs: 'none', md: 'block' } }}>
                      <span className="num" style={{
                        color: Number(p.buckets[b]) ? TONE[b] : c.inkMuted,
                        display: 'block' }}>
                        {Number(p.buckets[b]) ? fmtMoney(p.buckets[b]) : '—'}
                      </span>
                    </Box>
                  ))}
                  <span className="num" style={{ fontWeight: 600 }}>
                    {fmtMoney(p.total)}</span>
                </Box>

                {open.has(p.ledger_code) && (
                  <Box sx={{ bgcolor: c.surfacePage, px: 2, pb: 1 }}>
                    {p.bills.map((b) => (
                      <Stack key={b.ref_name} direction="row" spacing={1.5}
                        alignItems="center"
                        onClick={() => b.voucher_id && setVoucher(b.voucher_id)}
                        sx={{ py: 0.65, pl: 4, cursor: b.voucher_id ? 'pointer' : 'default',
                          '&:hover': { bgcolor: c.primarySoft } }}>
                        <Typography sx={{ fontSize: 12.5, minWidth: 120 }} noWrap>
                          {b.ref_name}</Typography>
                        <span className="num" style={{ fontSize: 11.5,
                          color: c.inkMuted, minWidth: 84, textAlign: 'start' }}>
                          {b.due_date ?? b.bill_date}</span>
                        {b.days_overdue > 0 ? (
                          <Typography sx={{ fontSize: 11, color: c.dangerInk,
                            minWidth: 74 }}>
                            {t('bills.daysOverdue', { n: b.days_overdue })}
                          </Typography>
                        ) : <Box sx={{ minWidth: 74 }} />}
                        <Box sx={{ flex: 1 }} />
                        <span className="num" style={{ fontSize: 11.5,
                          color: c.inkMuted }}>
                          {fmtMoney(b.original)}</span>
                        <span className="num" style={{ fontSize: 12.5,
                          minWidth: 100, fontWeight: 500 }}>
                          {fmtMoney(b.outstanding)}</span>
                      </Stack>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {!!rep?.parties.length && !isPhone && (
          <Box sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 1,
            px: 2, py: 1.25, borderTop: `1px solid ${c.ink}` }}>
            <span className="section-label">{t('report.total')}</span>
            {BUCKETS.map((b) => (
              <Box key={b} sx={{ display: { xs: 'none', md: 'block' } }}>
                <span className="num" style={{ fontWeight: 600, color: TONE[b],
                  display: 'block' }}>{fmtMoney(rep.totals[b])}</span>
              </Box>
            ))}
            <span className="num" style={{ fontWeight: 600 }}>
              {fmtMoney(rep.grand_total)}</span>
          </Box>
        )}

        {!!rep?.parties.length && isPhone && (
          <Stack direction="row" justifyContent="space-between"
            sx={{ px: 2, py: 1.5, borderTop: `1px solid ${c.ink}` }}>
            <span className="section-label">{t('report.total')}</span>
            <span className="num" style={{ fontWeight: 600, fontSize: 16 }}>
              {fmtMoney(rep.grand_total)}</span>
          </Stack>
        )}
        <ReportFooter />
      </ReportShell>

      <VoucherDrawer voucherId={voucher} onClose={() => setVoucher(null)}
        onLedgerClick={onLedger} />
    </>
  )
}
